import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../services/api.ts';
import type { Character } from '../services/api.ts';

type StoryStatusLike = {
  current_step?: string;
  character_phase?: string;
  character_count?: number;
};

const allDone = (chars: Character[]) =>
  chars.length > 0 && chars.every(c => c.status === 'completed' && !!c.image_url);

export function useCharacterImagesQuery(
  storyId: number | null,
  status?: StoryStatusLike | null
) {
  const enabled = Boolean(storyId);

  return useQuery<Character[], Error>({
    queryKey: ['character-images', storyId],
    queryFn: async () => {
      if (!storyId) return [];
      const res = await apiGet<any[]>(`/runs/${storyId}/character-images`);
      if ((res as any)?.error) throw new Error((res as any).error);
      const raw = (res as any)?.data ?? [];

      // normalize + defaults
      return raw.map((c: any, i: number) => ({
        id: c.id ?? i + 1,
        name: c.name,
        description: c.description ?? '',
        image_url: c.image_url ?? null,
        status: (c.status ?? 'generating') as 'generating' | 'completed',
        order_index: c.order_index ?? i,
        character_type: c.character_type ?? 'main',
        created_at: c.created_at ?? undefined,
      })) as Character[];
    },
    enabled,
    // Poll while characters are still missing images, even if the step has moved to scenes (reuse flow)
    refetchInterval: (q) => {
      const data = (q.state.data as Character[]) ?? [];
      const hasMissing = data.length === 0 || !allDone(data);
      const onRelevantStep = status?.current_step === 'characters' || status?.current_step === 'scenes';
      const notCreating = status?.character_phase !== 'creating_story';
      return onRelevantStep && notCreating && hasMissing ? 2000 : false;
    },
    refetchOnWindowFocus: false,
    // completed data can stay fresh longer; WIP stays hot
    staleTime:
      (status?.current_step === 'characters' || status?.current_step === 'scenes') ? 0 : 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // keep in cache for quick back/forward nav
    placeholderData: (prev) => prev, // no flicker on route changes
    select: (data) => data.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
  });
}
